// file: src/components/OrderBulkActionBar.jsx
import React from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  Switch,
  Tooltip,
  Spacer,
} from "@chakra-ui/react";

const OrderBulkActionBar = ({
  totalSelected,
  packingCompleteCount,
  packingMissingCount,
  trackingCompleteCount,
  trackingMissingCount,
  requireTrackingForCombine,
  onToggleRequireTracking,
  onClearSelection,
  onCompletePackingSlips,
  onCombinePackingSlips,
  canCombine,
  combineInProgress = false,
}) => {
  const hasMissingPacking = packingMissingCount > 0;
  const hasMissingTracking = trackingMissingCount > 0;

  return (
    <Box
      position="fixed"
      left={0}
      right={0}
      bottom={0}
      zIndex={1400}
      bg="white"
      borderTopWidth="1px"
      boxShadow="0 -4px 16px rgba(0,0,0,0.08)"
      px={[4, 6, 8]}
      py={3}
    >
      <HStack align="center" spacing={4} flexWrap="wrap">
        {/* Summary */}
        <VStack align="flex-start" spacing={1}>
          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
            Bulk Order Actions
          </Text>
          <HStack spacing={2} flexWrap="wrap">
            <Badge colorScheme="teal" variant="subtle">
              Selected: {totalSelected}
            </Badge>
            <Badge
              colorScheme={hasMissingPacking ? "orange" : "green"}
              variant="subtle"
            >
              Packing lists: {packingCompleteCount} / {totalSelected}
            </Badge>
            {requireTrackingForCombine && (
              <Badge
                colorScheme={hasMissingTracking ? "red" : "green"}
                variant="subtle"
              >
                Tracking numbers: {trackingCompleteCount} / {totalSelected}
              </Badge>
            )}
          </HStack>
          <Text fontSize="xs" color="gray.600">
            Complete a packing list for each selected order. You can leave
            tracking numbers blank unless you choose to require them for
            combining.
          </Text>
        </VStack>

        <Spacer />

        {/* Tracking requirement toggle */}
        <HStack spacing={2}>
          <Switch
            size="sm"
            isChecked={requireTrackingForCombine}
            onChange={(e) => onToggleRequireTracking(e.target.checked)}
            colorScheme="teal"
          />
          <Text fontSize="xs" color="gray.700">
            Require tracking numbers to combine
          </Text>
        </HStack>

        {/* Actions */}
        <HStack spacing={2}>
          <Tooltip
            label={
              hasMissingPacking
                ? "Open the next order that still needs a packing list."
                : "All selected orders already have packing lists."
            }
            hasArrow
          >
            <Button
              size="sm"
              variant="outline"
              onClick={onCompletePackingSlips}
              isDisabled={!hasMissingPacking}
              _hover={{ transform: "scale(1)" }}
            >
              Complete packing lists
            </Button>
          </Tooltip>

          <Tooltip
            label={
              canCombine
                ? "Combine packing lists into a single, saved MedicalCombined packing list PDF."
                : requireTrackingForCombine
                ? "All selected orders must have packing lists and tracking numbers to combine."
                : "All selected orders must have packing lists to combine."
            }
            hasArrow
          >
            <Button
              size="sm"
              colorScheme="purple"
              onClick={onCombinePackingSlips}
              isDisabled={!canCombine || combineInProgress}
              isLoading={combineInProgress}
              loadingText="Combining..."
              _hover={{ transform: "scale(1)" }}
            >
              Combine into single PDF
            </Button>
          </Tooltip>

          <Button
            size="sm"
            variant="ghost"
            onClick={onClearSelection}
            _hover={{ transform: "scale(1)" }}
          >
            Clear selection
          </Button>
        </HStack>
      </HStack>
    </Box>
  );
};

export default OrderBulkActionBar;
